import React, { useCallback } from 'react';
// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
import { fetchQuery, graphql, useRelayEnvironment } from 'react-relay';
import { fireErrorAnalytics } from '@jira/platform__error-reporting/src/fireErrorAnalytics.tsx';
import { expVal } from '@atlassian/jira-feature-experiments';
import type {
	AIResponse,
	FeedbackContext,
} from '@atlassian/jira-atlassian-intelligence/src/common/types.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import FetchError from '@atlassian/jira-fetch/src/utils/errors.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { useAnalyticsEvents, fireTrackAnalytics } from '@atlassian/jira-product-analytics-bridge';
import { useProjectContext } from '@atlassian/jira-providers-project-context/src/index.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { AI_FEATURE_NAME, PACKAGE_NAME, TEAM_NAME } from '../../common/constants.tsx';
import type { useNaturalLanguageToJqlQuery } from './__generated__/useNaturalLanguageToJqlQuery.graphql';
import { MessageWithFeedbackButton } from './message-with-feedback-button/index.tsx';
import { MessageWithSupportLink } from './message-with-support-link/index.tsx';
import messages from './messages.tsx';

const RATE_LIMIT_SUPPORT_LINK =
	'https://support.atlassian.com/organization-administration/docs/usage-limits-in-atlassian-intelligence/';
const SERVER_ERROR_SUPPORT_LINK =
	'https://support.atlassian.com/organization-administration/docs/troubleshooting-for-atlassian-intelligence/';

const fireNLErrorAnalytics = (
	errorType: 'graphql_error' | 'server_error' | 'failed_response' | 'rate_limit_error',
	error?: Error,
) => {
	fireErrorAnalytics({
		attributes: {
			errorType,
		},
		meta: {
			id: 'useNaturalLanguageToJqlQuery',
			packageName: PACKAGE_NAME,
			teamName: TEAM_NAME,
		},
		error,
		sendToPrivacyUnsafeSplunk: true,
	});
};

export const useNaturalLanguageToJql = () => {
	const { formatMessage } = useIntl();
	const cloudId = useCloudId();
	const environment = useRelayEnvironment();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const projectContext = useProjectContext();

	const projectKey = fg('jira_nl2jql_forward_proejct_context')
		? projectContext.data?.projectKey || null
		: null;

	return useCallback(
		async (prompt: string, promptId: string): Promise<AIResponse<string>> => {
			/**
			 * The attributes populated for centralised AI MAU calculation is mentioned here:
			 * https://hello.atlassian.net/wiki/spaces/AAI/pages/3115805726/Single+Instrumentation+for+AI+MAU
			 * Note: the 1 in userGeneratedAI represents a 'boolean' sort of value
			 */
			fireTrackAnalytics(createAnalyticsEvent({}), 'aiInteraction initiated', {
				aiFeatureName: AI_FEATURE_NAME,
				userGeneratedAI: 1,
				proactiveAIGenerated: 0,
				singleInstrumentationID: promptId,
				isAIFeature: 1,
			});

			return fetchQuery<useNaturalLanguageToJqlQuery>(
				environment,
				// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
				graphql`
					query useNaturalLanguageToJqlQuery(
						$cloudId: ID!
						$naturalLanguageInput: String!
						$iteration: JiraIteration!
						$project: String
					) {
						jira @required(action: THROW) {
							naturalLanguageToJql(
								cloudId: $cloudId
								input: {
									naturalLanguageInput: $naturalLanguageInput
									iteration: $iteration
									searchContext: { projectKey: $project }
								}
							) @optIn(to: "JiraNaturalLanguageToJQL") @required(action: THROW) {
								generatedJQL
								generatedJQLError {
									... on JiraUIExposedError {
										__typename
										message
									}
									... on JiraUsageLimitExceededError {
										__typename
									}
									... on JiraGeneratedJqlInvalidError {
										__typename
									}
									... on JiraUnsupportedLanguageError {
										__typename
									}
									... on JiraServerError {
										__typename
									}
								}
							}
						}
					}
				`,
				{
					cloudId,
					naturalLanguageInput: prompt,
					iteration: 'ITERATION_2',
					project: projectKey,
				},
			)
				.toPromise()
				.then((data) => {
					const invalidJqlErrorNonFinalIssueTerminologyRefreshM4Galaxia = expVal(
						'project-terminology-refresh',
						'isEnabled',
						false,
					)
						? messages.invalidJqlErrorNonFinalIssueTerminologyRefreshM4Galaxia
						: messages.invalidJqlErrorNonFinalIssueTerminologyRefreshM4;
					if (data) {
						const {
							jira: {
								naturalLanguageToJql: { generatedJQL, generatedJQLError },
							},
						} = data;

						if (generatedJQL != null) {
							const additionalFeedbackContext: FeedbackContext[] = [
								{ id: 'JQL Response', value: generatedJQL },
							];
							return {
								type: 'success',
								is5xxError: false,
								payload: generatedJQL,
								additionalFeedbackContext,
							} as const;
						}
						// If we have no JQL or relevant errors then show a warning message
						if (generatedJQLError == null) {
							// TODO - revise error type: JNLIS-488
							fireNLErrorAnalytics('failed_response');
							return {
								type: 'warning' as const,
								is5xxError: false,
								payload: (
									<MessageWithFeedbackButton
										message={formatMessage(messages.noJqlReturned)}
										linkText={formatMessage(messages.helpUsImproveNonFinal)}
									/>
								),
							};
						}

						if (generatedJQLError?.__typename === 'JiraGeneratedJqlInvalidError') {
							// TODO - revise error type: JNLIS-488
							fireNLErrorAnalytics('server_error');
							return {
								type: 'error' as const,
								is5xxError: false,
								payload: (
									<MessageWithFeedbackButton
										message={formatMessage(
											fg('jira-issue-terminology-update-m4')
												? invalidJqlErrorNonFinalIssueTerminologyRefreshM4Galaxia
												: messages.invalidJqlErrorNonFinal,
										)}
										linkText={formatMessage(messages.helpUsImproveNonFinal)}
									/>
								),
							};
						}

						if (generatedJQLError?.__typename === 'JiraUnsupportedLanguageError') {
							// TODO - revise error type: JNLIS-488
							fireNLErrorAnalytics('server_error');
							return {
								type: 'error' as const,
								is5xxError: false,
								payload: (
									<MessageWithFeedbackButton
										message={formatMessage(messages.unsupportedLanguageErrorNonFinal)}
										linkText={formatMessage(messages.helpUsImproveNonFinal)}
									/>
								),
							};
						}

						if (generatedJQLError?.__typename === 'JiraUsageLimitExceededError') {
							fireNLErrorAnalytics('rate_limit_error');

							return {
								type: 'error' as const,
								is5xxError: false,
								payload: (
									<MessageWithSupportLink
										message={messages.errorRateLimit}
										href={RATE_LIMIT_SUPPORT_LINK}
									/>
								),
							};
						}

						if (
							generatedJQLError?.__typename === 'JiraUIExposedError' &&
							generatedJQLError?.message != null
						) {
							fireNLErrorAnalytics('server_error');
							return {
								type: 'error' as const,
								is5xxError: false,
								payload: (
									<MessageWithFeedbackButton
										message={generatedJQLError.message}
										linkText={formatMessage(messages.helpUsImproveNonFinal)}
									/>
								),
							};
						}

						if (generatedJQLError?.__typename === 'JiraServerError') {
							fireNLErrorAnalytics('server_error');
							return {
								type: 'error' as const,
								is5xxError: true,
								payload: (
									<MessageWithSupportLink
										message={messages.serverError}
										href={SERVER_ERROR_SUPPORT_LINK}
									/>
								),
							};
						}
					}
					// TODO - revise error type: JNLIS-488
					fireNLErrorAnalytics('server_error');
					return {
						type: 'error' as const,
						is5xxError: true,
						payload: (
							<MessageWithSupportLink
								message={messages.serverError}
								href={SERVER_ERROR_SUPPORT_LINK}
							/>
						),
					};
				})
				.catch((error) => {
					if (error instanceof FetchError && error.statusCode === 429) {
						fireNLErrorAnalytics('rate_limit_error', error);

						return {
							type: 'error' as const,
							is5xxError: false,
							payload: (
								<MessageWithSupportLink
									message={messages.errorRateLimit}
									href={RATE_LIMIT_SUPPORT_LINK}
								/>
							),
						};
					}
					// TODO - revise error type: JNLIS-488
					fireNLErrorAnalytics('graphql_error', error);
					return {
						type: 'error' as const,
						is5xxError: true,
						payload: (
							<MessageWithSupportLink
								message={messages.serverError}
								href={SERVER_ERROR_SUPPORT_LINK}
							/>
						),
					};
				});
		},
		[cloudId, createAnalyticsEvent, environment, formatMessage, projectKey],
	);
};
